// Itens de navegação por persona: derivam do registro de módulos e só aparecem com permissão (Parte 14.2).
import type { NamespaceKey } from '@giyus/i18n';

import { ROUTES } from '../lib/context/contextResolver';
import type { Permission } from '../lib/permissions';
import { MODULES } from '../modules/registry';

export interface NavItem {
  readonly key: string;
  readonly to: string;
  readonly labelKey: NamespaceKey<'shell'>;
  readonly end?: boolean;
}

/** `null` = sempre visível; uma permissão; ou uma lista any-of (basta uma). */
export function entryVisible(
  permission: Permission | readonly Permission[] | null,
  permissions: ReadonlySet<string>,
): boolean {
  if (permission === null) {
    return true;
  }
  return typeof permission === 'string'
    ? permissions.has(permission)
    : permission.some((item) => permissions.has(item));
}

/** Itens visíveis para o escopo ativo (rede ou tenant) dado o conjunto de permissões. */
export function buildNavItems(
  scope: 'network' | 'tenant',
  tenantCode: string | null,
  permissions: ReadonlySet<string>,
): readonly NavItem[] {
  const base = scope === 'network' ? ROUTES.network : tenantCode ? ROUTES.tenant(tenantCode) : null;
  if (!base) {
    return [];
  }
  const items: NavItem[] = [{ key: 'home', to: base, labelKey: 'nav.home', end: true }];
  for (const module of MODULES) {
    // REGRA 12 / 18 / 19:
    // 1. Não existe menu dedicado a "Tenant" na navegação lateral — um Tenant é um Centro de Pesquisa GIYUS (Não-Holding) e a GIYUS Holding controla todos os Centros/Tenants.
    // 2. Quando um Tenant (Centro GIYUS) está selecionado (scope === 'tenant'), menus exclusivos da Holding (ex.: Caixa Compartilhada corporativa @giyus.com.br) não existem no escopo do Tenant.
    if (module.key === 'tenancy') {
      continue;
    }
    if (scope === 'tenant' && module.key === 'shared-mailboxes') {
      continue;
    }
    const entry = scope === 'network' ? module.network : module.tenant;
    if (!entry) {
      continue;
    }
    if (!entryVisible(entry.permission, permissions)) {
      continue;
    }
    items.push({
      key: module.key,
      to: `${base}/${entry.path}`,
      labelKey: `nav.modules.${entry.labelKey}`,
    });
  }
  if (scope === 'network') {
    if (permissions.has('settings.read')) {
      items.push({ key: 'settings', to: `${base}/settings`, labelKey: 'nav.modules.settings' });
    }
    if (permissions.has('platform.health.read')) {
      items.push({
        key: 'platform-health',
        to: `${base}/platform-health`,
        labelKey: 'nav.modules.platformHealth',
      });
    }
  }
  return items;
}
